import { useMemo } from "react";
import { hasOwn } from "../aiUsage/hasOwn";

export interface DayValues {
  day: string; // YYYY-MM-DD
  values: Record<string, number>;
}

interface Props {
  title: string;
  days: DayValues[];
  /** Stacking and legend order, largest first. Past the fifth, series fold into one. */
  series: string[];
  labelFor: (key: string) => string;
  formatValue: (n: number) => string;
  emptyText: string;
  /** The name of the series past the fifth, folded into one. */
  otherLabel?: string;
  /**
   * A series that is itself the caller's "everything else" (the overview's
   * "~other"). It is drawn as the fold, last and in the fold colour, merged
   * with any series past the fifth; it never takes a series' colour.
   */
  foldKey?: string;
  /** A day's tooltip. Defaults to the day and its formatted total. */
  dayTitle?: (day: string, total: number) => string;
  /**
   * A legend entry's value, given the series keys it sums (one, or every key
   * in the fold) and its total. Defaults to formatValue(total).
   */
  legendValue?: (keys: string[], total: number) => string;
  height?: number;
}

const PALETTE = [
  "var(--accent, #8ab4ff)",
  "var(--green, #4ec27a)",
  "var(--amber, #e0a64a)",
  "var(--purple, #b48cff)",
  "var(--red, #f06b62)",
];
const FOLD_COLOR = "var(--muted, #8a8e96)";
const MAX_SERIES = 5;

// A day's value for one series. The keys come from the server (a tool key can
// be "constructor", a model name is free text), so only own keys count: a
// plain lookup would read Object.prototype's member for a day without the key.
function valueOf(values: Record<string, number>, key: string): number {
  const n = hasOwn(values, key) ? values[key] : 0;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;
}

// A stacked bar per day, drawn by hand like VerdictVolumeChart (no chart
// library in the Security Room).
export function DailyStackChart({
  title,
  days,
  series,
  labelFor,
  formatValue,
  emptyText,
  otherLabel = "Other",
  foldKey,
  dayTitle,
  legendValue,
  height = 170,
}: Props) {
  const W = 720;
  const H = height;
  const gutterL = 48;
  const gutterB = 22;
  const padTop = 12;
  const padR = 8;

  const model = useMemo(() => {
    const unique = [...new Set(series)].filter((k) => k !== foldKey);
    const shown = unique.slice(0, MAX_SERIES);
    const rest = unique.slice(MAX_SERIES);
    if (foldKey !== undefined && series.includes(foldKey)) rest.push(foldKey);
    // A column is one series, or (key null) the fold of every series past the
    // fifth and of the caller's fold key. The fold has no key of its own, so
    // no series name can be taken for it.
    const columns: Array<string | null> = rest.length ? [...shown, null] : shown;
    const rows = days.map((d) => {
      const vals = columns.map((k) =>
        k === null ? rest.reduce((s, x) => s + valueOf(d.values, x), 0) : valueOf(d.values, k)
      );
      return { day: d.day, vals, total: vals.reduce((a, b) => a + b, 0) };
    });
    const totals = columns.map((_, i) => rows.reduce((s, r) => s + r.vals[i], 0));
    return { columns, rest, rows, totals, max: Math.max(0, ...rows.map((r) => r.total)) };
  }, [days, series, foldKey]);

  const nameOf = (k: string | null) => (k === null ? otherLabel : labelFor(k));
  const valueOfColumn = (i: number) => {
    const k = model.columns[i];
    return legendValue
      ? legendValue(k === null ? model.rest : [k], model.totals[i])
      : formatValue(model.totals[i]);
  };
  const colorOf = (i: number) =>
    model.columns[i] === null ? FOLD_COLOR : PALETTE[i % PALETTE.length];
  const plotBottom = H - gutterB;
  const plotH = Math.max(1, plotBottom - padTop);
  const slot = (W - padR - gutterL) / Math.max(1, model.rows.length);
  const barW = Math.max(1, slot * 0.7);
  const hasData = model.max > 0;
  const summary = hasData
    ? model.columns.map((k, i) => `${nameOf(k)} ${valueOfColumn(i)}`).join(", ")
    : emptyText;

  return (
    <div className="sr-chart">
      <div className="sr-chart__head">
        <div className="sr-chart__title">{title}</div>
        <div className="sr-chart__legend">
          {model.columns.map((k, i) => (
            <span key={i} className="sr-legend">
              <i style={{ background: colorOf(i) }} />
              {nameOf(k)} {valueOfColumn(i)}
            </span>
          ))}
        </div>
      </div>
      <svg
        className="sr-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${title}: ${summary}`}
      >
        <line
          x1={gutterL}
          x2={W - padR}
          y1={plotBottom}
          y2={plotBottom}
          stroke="var(--line, #26272c)"
        />
        {hasData ? (
          <>
            <text
              x={gutterL - 6}
              y={padTop + 8}
              textAnchor="end"
              fill="var(--muted, #8a8e96)"
              fontSize="9"
            >
              {formatValue(model.max)}
            </text>
            {model.rows.map((r, i) => {
              const x = gutterL + slot * i + (slot - barW) / 2;
              let cursor = plotBottom;
              return (
                <g key={r.day} data-day={r.day}>
                  <title>
                    {dayTitle ? dayTitle(r.day, r.total) : `${r.day}: ${formatValue(r.total)}`}
                  </title>
                  {r.vals.map((v, si) => {
                    if (v <= 0) return null;
                    const h = Math.max(1.5, (v / model.max) * plotH);
                    cursor -= h;
                    return (
                      <rect key={si} x={x} y={cursor} width={barW} height={h} fill={colorOf(si)} />
                    );
                  })}
                </g>
              );
            })}
          </>
        ) : (
          <text x={W / 2} y={H / 2} textAnchor="middle" fill="var(--muted, #8a8e96)" fontSize="13">
            {emptyText}
          </text>
        )}
        {model.rows.length > 0 && (
          <>
            <text x={gutterL} y={H - 6} fill="var(--muted, #8a8e96)" fontSize="10">
              {model.rows[0].day.slice(5)}
            </text>
            <text
              x={W - padR}
              y={H - 6}
              textAnchor="end"
              fill="var(--muted, #8a8e96)"
              fontSize="10"
            >
              {model.rows[model.rows.length - 1].day.slice(5)}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
